import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Permission } from '@app/core/permission/enums/permission.enum';
import { PermissionService } from '@app/core/permission/services/permission.service';

/**
 * Utility function to redirect to a not-found page when unauthorized.
 * @param router The Angular Router service.
 * @returns `false` to prevent route activation.
 */
export function handleUnauthorized(router: Router): boolean {
  // User is not authorized for this route, redirecting to a 'not-found' page
  // to prevent revealing the existence of a route they shouldn't access.
  router.navigate(['/not-found']);
  return false;
}

/**
 * Higher-order function to create a `CanActivateFn` guard that checks for a specific permission.
 * This factory approach promotes reusability for permission-based route protection.
 * @param requiredPermission The permission required to activate the route.
 * @returns A `CanActivateFn` that returns `true` if the user has the permission, otherwise redirects and returns `false`.
 */
export function hasPermissionGuard(requiredPermission: Permission): CanActivateFn {
  return () => {
    // Dependency injection using the `inject` function, as per modern Angular standards.
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    // Check if the user has the required permission.
    if (permissionService.hasPermission(requiredPermission)) {
      return true;
    }

    return handleUnauthorized(router);
  };
}
